import { all, takeEvery, put, call } from 'redux-saga/effects'
import { notification } from 'antd'
import actions from './actions'
import {
  listRoles,
  createRole,
  fetchRole,
  updateRole,
  deleteRole,
  fetchRolePermissions,
  assignPermissionToRole,
  removePermissionFromRole,
  fetchUserRoles,
  assignRoleToUser,
  removeRoleFromUser,
} from '../../services/role'

function* handleFetchRoles() {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(listRoles)
    yield put({
      type: actions.FETCH_ROLES_RECEIVED,
      payload: result.data || result,
    })
  } catch (error) {
    console.error('Error fetching roles:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to fetch roles',
    })
  }
}

function* handleCreateRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(createRole, payload)
    yield put({
      type: actions.CREATE_ROLE_RECEIVED,
      payload: result.data || result,
    })
    notification.success({
      message: 'Success',
      description: 'Role created successfully',
    })
    // Refresh roles list
    yield put({ type: actions.FETCH_ROLES })
  } catch (error) {
    console.error('Error creating role:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to create role',
    })
  }
}

function* handleFetchRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(fetchRole, payload)
    yield put({
      type: actions.FETCH_ROLE_RECEIVED,
      payload: result.data || result,
    })
  } catch (error) {
    console.error('Error fetching role:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to fetch role',
    })
  }
}

function* handleUpdateRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(updateRole, payload.id, payload)
    yield put({
      type: actions.UPDATE_ROLE_RECEIVED,
      payload: result.data || result,
    })
    notification.success({
      message: 'Success',
      description: 'Role updated successfully',
    })
    // Refresh roles list
    yield put({ type: actions.FETCH_ROLES })
  } catch (error) {
    console.error('Error updating role:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to update role',
    })
  }
}

function* handleDeleteRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    yield call(deleteRole, payload)
    yield put({
      type: actions.DELETE_ROLE_RECEIVED,
      payload,
    })
    notification.success({
      message: 'Success',
      description: 'Role deleted successfully',
    })
  } catch (error) {
    console.error('Error deleting role:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to delete role',
    })
  }
}

function* handleFetchRolePermissions({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(fetchRolePermissions, payload)
    yield put({
      type: actions.FETCH_ROLE_PERMISSIONS_RECEIVED,
      payload: result.data || result,
    })
  } catch (error) {
    console.error('Error fetching role permissions:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
  }
}

function* handleAssignPermissionToRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    yield call(assignPermissionToRole, payload.roleId, payload)
    // Re-fetch permissions from server to ensure UI matches backend
    yield put({ type: actions.FETCH_ROLE_PERMISSIONS, payload: payload.roleId })
  } catch (error) {
    console.error('Error assigning permission:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to assign permission',
    })
  }
}

function* handleRemovePermissionFromRole({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(removePermissionFromRole, payload.roleId, payload.permissionId)
    if (result === null || result === undefined) {
      throw new Error('Delete request failed')
    }
    // Re-fetch permissions from server to ensure UI matches backend
    yield put({ type: actions.FETCH_ROLE_PERMISSIONS, payload: payload.roleId })
  } catch (error) {
    console.error('Error removing permission:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to remove permission',
    })
    // Re-fetch to revert optimistic state
    yield put({ type: actions.FETCH_ROLE_PERMISSIONS, payload: payload.roleId })
  }
}

// ──── User-Role Saga Handlers ────

function* handleFetchUserRoles({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(fetchUserRoles, payload)
    yield put({
      type: actions.FETCH_USER_ROLES_RECEIVED,
      payload: result.data || result,
    })
  } catch (error) {
    console.error('Error fetching user roles:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
  }
}

function* handleAssignRoleToUser({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(assignRoleToUser, payload)
    yield put({
      type: actions.ASSIGN_ROLE_TO_USER_RECEIVED,
      payload: result.data || result,
    })
    notification.success({
      message: 'Success',
      description: 'Role assigned to user',
    })
    // Refresh user roles
    yield put({ type: actions.FETCH_USER_ROLES, payload: payload.userId })
  } catch (error) {
    console.error('Error assigning role to user:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to assign role to user',
    })
  }
}

function* handleRemoveRoleFromUser({ payload }) {
  try {
    yield put({ type: actions.REQUESTING })
    const result = yield call(removeRoleFromUser, payload.userId, payload.roleId)
    if (result === null || result === undefined) {
      throw new Error('Delete request failed')
    }
    yield put({
      type: actions.REMOVE_ROLE_FROM_USER_RECEIVED,
      payload,
    })
    notification.success({
      message: 'Success',
      description: 'Role removed from user',
    })
    // Re-fetch user roles to ensure UI is in sync with server
    yield put({ type: actions.FETCH_USER_ROLES, payload: payload.userId })
  } catch (error) {
    console.error('Error removing role from user:', error)
    yield put({
      type: actions.RESPONSE_ERROR,
      payload: error.message,
    })
    notification.error({
      message: 'Error',
      description: 'Failed to remove role from user',
    })
    // Re-fetch to revert optimistic update
    yield put({ type: actions.FETCH_USER_ROLES, payload: payload.userId })
  }
}

function* watchRoles() {
  yield all([
    takeEvery(actions.FETCH_ROLES, handleFetchRoles),
    takeEvery(actions.CREATE_ROLE, handleCreateRole),
    takeEvery(actions.FETCH_ROLE, handleFetchRole),
    takeEvery(actions.UPDATE_ROLE, handleUpdateRole),
    takeEvery(actions.DELETE_ROLE, handleDeleteRole),
    takeEvery(actions.FETCH_ROLE_PERMISSIONS, handleFetchRolePermissions),
    takeEvery(actions.ASSIGN_PERMISSION_TO_ROLE, handleAssignPermissionToRole),
    takeEvery(actions.REMOVE_PERMISSION_FROM_ROLE, handleRemovePermissionFromRole),
    takeEvery(actions.FETCH_USER_ROLES, handleFetchUserRoles),
    takeEvery(actions.ASSIGN_ROLE_TO_USER, handleAssignRoleToUser),
    takeEvery(actions.REMOVE_ROLE_FROM_USER, handleRemoveRoleFromUser),
  ])
}

export default watchRoles
